import React, { useState } from "react";
import { ChevronDown, ScrollText, UserRound } from "lucide-react";
import { relTime } from "../apikeys/shared";
import { actorName } from "../modules/util";
import { fmtDate } from "../../lib/origin.jsx";
import { cn } from "../../lib/utils";

function fmtValue(v) {
  if (v.unit === "probability" || v.unit === "share of units / year") return `${+(v.value * 100).toFixed(1)}%`;
  if (v.unit === "°C") return `${v.value}°C`;
  return `${v.value}`;
}

function LastChange({ change }) {
  if (!change) return <div className="text-[10.5px] text-subtle">Default setting · not changed since launch</div>;
  const what = change.kind === "setting" ? `${change.label}: ${change.before} → ${change.after}`
    : change.kind === "install_upload" ? `Install base upload · ${change.accepted} rows`
    : change.kind === "link_import" ? `Supersession import · ${change.accepted} links`
    : `Supersession link ${change.kind.replace("link_", "")}`;
  return (
    <div className="text-[10.5px] text-muted leading-snug">
      <span className="inline-flex items-center gap-1 text-fg/80"><UserRound size={10} /> {actorName(change.actor)}</span>
      {" · "}<span title={change.ts}>{relTime(change.ts)}</span>{" · "}{what}
      <div className="italic text-subtle mt-0.5">“{change.reason}”</div>
    </div>
  );
}

export function RuleCard({ rule, testid }) {
  const valueKeys = rule.values.filter(v => !v.key.startsWith("replacement_rate."));
  return (
    <div data-testid={testid} className="border border-border bg-surface-2/30 rounded-sm p-3.5 flex flex-col gap-2 hover:border-border-strong transition-colors">
      <div className="flex items-baseline gap-2">
        <span className="font-mono-tabular text-[10px] text-primary font-semibold">{String(rule.step).padStart(2, "0")}</span>
        <span className="text-[12.5px] font-semibold leading-tight">{rule.title}</span>
      </div>
      <p className="text-[11.5px] text-fg/85 leading-relaxed">{rule.statement}</p>
      {valueKeys.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {valueKeys.map(v => (
            <span key={v.key} data-testid={`${testid}-value-${v.key}`}
                  className="inline-flex items-baseline gap-1 px-2 py-0.5 border border-primary/40 bg-primary/10 rounded-sm text-[10.5px]">
              <span className="text-muted">{v.label}</span>
              <strong className="font-mono-tabular text-primary">{fmtValue(v)}</strong>
            </span>
          ))}
        </div>
      )}
      <div className="text-[11px] text-fg/70 border-l-2 border-primary/40 pl-2">{rule.effect}</div>
      <div className="mt-auto pt-2 border-t border-border/60"><LastChange change={rule.last_change} /></div>
    </div>
  );
}

// Expandable AI Policy Rules statement — published rule set behind every class.
export default function PolicyRulesPanel({ rules, run, testid = "policy-rules", defaultOpen = false, className }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!rules) return null;
  return (
    <div data-testid={testid} className={cn("tile", className)}>
      <button data-testid={`${testid}-toggle`} onClick={() => setOpen(o => !o)} aria-expanded={open}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-surface-2/40 transition-colors">
        <span className="flex items-center gap-2 min-w-0">
          <ScrollText size={13} className="text-primary shrink-0" />
          <span className="text-[11px] font-semibold tracking-widest uppercase text-muted">AI Policy Rules</span>
          <span className="text-[10.5px] text-subtle truncate hidden sm:inline">· {rules.length} rules applied in order · every class traces to a published rule</span>
        </span>
        <span className="flex items-center gap-3 shrink-0">
          {run && <span className="text-[10px] text-subtle font-mono-tabular hidden md:inline">Run {run.run_id} · {fmtDate(run.ran_at)}</span>}
          <ChevronDown size={14} className={cn("text-muted transition-transform duration-200", open && "rotate-180")} />
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4 gap-3 animate-fade-in" data-testid={`${testid}-cards`}>
          {rules.map(r => <RuleCard key={r.id} rule={r} testid={`${testid}-rule-${r.id}`} />)}
        </div>
      )}
    </div>
  );
}
